import React, { useEffect, useRef } from 'react';
import { useLatest } from 'ahooks';

import { DocumentMode } from '../models/Types';
import { FULLSTORY_EXCLUDE } from '../constants/analytics';

export const IFRAME_ID = 'zdocsIframe';

export enum IFRAME_EVENTS {
  ReviewComplete = 'reviewComplete',
  DocumentSaved = 'documentSaved',
  DocumentSavedSummary = 'documentSavedSummary',
  DocumentRevisionRefresh = 'documentRevisionRefresh',
  FieldClicked = 'fieldClicked',
  FieldPlaced = 'fieldPlaced',
  FieldRemoved = 'fieldRemoved',
  ModalOpened = 'modalOpened',
  ModalClosed = 'modalClosed',
  Error = 'error',
}

export enum IFRAME_ERROR_TYPES {
  MissingToken = 'missingToken',
  InvalidToken = 'invalidToken',
  ExpiredToken = 'expiredToken',
  FetchError = 'fetchError',
  SaveError = 'saveError',
  PageImageError = 'pageImageError',
  Unknown = 'unknownError',
}

export enum IFRAME_COMMANDS {
  SAVE_DOCUMENT = 'saveDocument',
}

type Props = {
  leaseId: string;
  documentUrl: string;
  documentMode: DocumentMode;
  saveLeaseEditorRequested?: boolean;
  cancelSaveLeaseEditor?(leaseId: string): void;
  onLoad(): void;
  onError(): void;
  onFieldPlaced?(): void;
  onFieldClicked?(): void;
  onFieldRemoved?(): void;
  onReviewComplete?(): void;
  onEditorSaved(): void;
  onEditorSaveError(): void;
  onSigningComplete(): void;
  onSigningError(): void;
  onExpiredToken?(): void;
};

export function ZDocsDocumentView(props: Props) {
  const iframeRef = useRef(null);
  const latestProps = useLatest(props);
  const sessionModeRef = useRef(props.documentMode);

  useEffect(() => {
    sessionModeRef.current = props.documentMode;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- a new url loads a new session; its mode arrives with it
  }, [props.documentUrl]);

  function messageListener(msg: MessageEvent) {
    const event: IFRAME_EVENTS | undefined = msg.data?.event;
    const errorType: IFRAME_ERROR_TYPES | undefined = msg.data?.meta?.error;
    const current = latestProps.current;

    if (event === IFRAME_EVENTS.Error) {
      if (errorType) {
        // eslint-disable-next-line no-console
        console.info('Received error:', errorType);
      }
    } else {
      if (event) {
        // eslint-disable-next-line no-console
        console.info('Received event:', event);
      }
    }

    const callbackMapping = {
      [IFRAME_EVENTS.FieldPlaced]: current.onFieldPlaced,
      [IFRAME_EVENTS.FieldClicked]: current.onFieldClicked,
      [IFRAME_EVENTS.FieldRemoved]: current.onFieldRemoved,
      [IFRAME_EVENTS.ReviewComplete]: current.onReviewComplete,
      [IFRAME_EVENTS.DocumentSavedSummary]: () => onDocumentSavedSummary(msg.data.meta.summary),
      [IFRAME_ERROR_TYPES.ExpiredToken]: current.onExpiredToken,
    };

    const callbackFunction = callbackMapping[event !== IFRAME_EVENTS.Error ? event : errorType];
    callbackFunction?.();
  }

  function onDocumentSavedSummary(documentSaveSummary: { [docId: string]: boolean }) {
    const success = !Object.values(documentSaveSummary).some((val) => !val);
    const current = latestProps.current;
    switch (sessionModeRef.current) {
      case DocumentMode.FILL:
        if (success) {
          current.onSigningComplete();
        } else {
          current.onSigningError();
        }
        return;
      case DocumentMode.EDIT:
        if (success) {
          current.onEditorSaved();
        } else {
          current.onEditorSaveError();
        }
        return;
    }
  }

  function onSaveEditorRequested() {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ event: IFRAME_COMMANDS.SAVE_DOCUMENT }, '*');
      props.cancelSaveLeaseEditor?.(props.leaseId);
    } else if (iframeRef.current) {
      // contentWindow may be null in test environments (JSDOM)
      props.cancelSaveLeaseEditor?.(props.leaseId);
    }
  }

  useEffect(() => {
    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- subscribed once; the listener reads latest props via ref
  }, []);

  useEffect(() => {
    if (props.saveLeaseEditorRequested) {
      onSaveEditorRequested();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [props.saveLeaseEditorRequested]);

  return props.documentUrl ? (
    <iframe
      className={FULLSTORY_EXCLUDE}
      src={props.documentUrl}
      ref={iframeRef}
      onLoad={props.onLoad}
      onError={props.onError}
      id={IFRAME_ID}
      title="ZDocs Document"
      style={{ height: '100%', width: '100%', border: 'none', display: 'block' }}
    />
  ) : null;
}

export default ZDocsDocumentView;
